import { useState, useCallback } from 'react';
import { Mail, Eye, Copy, Code } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { previewEmailTemplate } from '@/services/queueApi.service';

export interface EmailCompletionTemplate {
  enabled: boolean;
  subject: string;
  htmlTemplate: string;
}

interface EmailTemplateEditorProps {
  value: EmailCompletionTemplate;
  onChange: (value: EmailCompletionTemplate) => void;
}

const TEMPLATE_VARIABLES = [
  { key: '{{nome_participante}}', description: 'Nome do participante', example: 'João Silva' },
  { key: '{{email_participante}}', description: 'Email do participante', example: 'joao@email.com' },
  { key: '{{nome_evento}}', description: 'Nome do evento', example: 'Evento Exemplo' },
  { key: '{{data_evento}}', description: 'Data do evento', example: '15/03/2026' },
  { key: '{{nome_fila}}', description: 'Nome da fila', example: 'Fila Principal' },
  { key: '{{data_ativacao}}', description: 'Data/hora da conclusão', example: '13/03/2026, 14:30' },
  { key: '{{posicao}}', description: 'Posição na fila', example: '5' },
];

const SAMPLE_DATA: Record<string, string> = {};
TEMPLATE_VARIABLES.forEach((v) => {
  SAMPLE_DATA[v.key] = v.example;
});

export function EmailTemplateEditor({ value, onChange }: EmailTemplateEditorProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const handleCopy = useCallback((key: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  }, []);

  const previewHtml = previewEmailTemplate(
    value.htmlTemplate || '',
    SAMPLE_DATA,
  );

  return (
    <div className="space-y-6">
      {/* Enable/Disable Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-muted-foreground" />
          <div>
            <Label htmlFor="email-enabled" className="text-base font-medium">
              Email de Conclusão
            </Label>
            <p className="text-sm text-muted-foreground">
              Enviar email automático quando participante é concluído
            </p>
          </div>
        </div>
        <Switch
          id="email-enabled"
          checked={value.enabled}
          onCheckedChange={(checked) =>
            onChange({ ...value, enabled: checked })
          }
        />
      </div>

      {value.enabled && (
        <>
          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="email-subject">Assunto do Email</Label>
            <Input
              id="email-subject"
              placeholder="Ex: Obrigado {{nome_participante}}! Seu atendimento foi concluído"
              value={value.subject}
              onChange={(e) =>
                onChange({ ...value, subject: e.target.value })
              }
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground">
              Variáveis também funcionam no assunto. Máx 200 caracteres.
            </p>
          </div>

          {/* HTML Template */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="email-html">
                <span className="flex items-center gap-1.5">
                  <Code className="h-4 w-4" />
                  Template HTML
                </span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(true)}
                disabled={!value.htmlTemplate}
              >
                <Eye className="h-4 w-4 mr-1.5" />
                Preview
              </Button>
            </div>
            <Textarea
              id="email-html"
              placeholder="<h1>Olá {{nome_participante}}</h1>\n<p>Seu atendimento no evento {{nome_evento}} foi concluído.</p>"
              value={value.htmlTemplate}
              onChange={(e) =>
                onChange({ ...value, htmlTemplate: e.target.value })
              }
              rows={20}
              className="font-mono text-sm"
            />
          </div>

          {/* Variables Reference */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-3">
                <Code className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Variáveis Disponíveis</span>
              </div>
              <div className="grid gap-2">
                {TEMPLATE_VARIABLES.map((v) => (
                  <div
                    key={v.key}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono text-xs">
                        {v.key}
                      </Badge>
                      <span className="text-muted-foreground">
                        {v.description}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2"
                      onClick={() => handleCopy(v.key)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                      {copiedKey === v.key && (
                        <span className="ml-1 text-xs text-status-ok">
                          Copiado
                        </span>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Preview Dialog */}
          <Dialog open={showPreview} onOpenChange={setShowPreview}>
            <DialogContent className="max-w-3xl max-h-[80vh]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Preview do Email
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                O preview é uma aproximação. Gmail/Outlook podem renderizar diferente.
              </p>
              <div className="border rounded-md overflow-hidden bg-card">
                <iframe
                  srcDoc={previewHtml}
                  sandbox=""
                  title="Email Preview"
                  className="w-full min-h-[400px] border-0"
                />
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
